import ProtectedRoute from '@/ProtectedRoute';
import { addCurrentPage, incrementStep, resetGoingBack, selectIsGoingBack } from '@/reducers/currentPage';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { store } from '@app/store';
import ExperienceService from '@services/ExperienceService';
import type { FC, ReactNode } from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { pageListOrder } from './defaultVerbiages';

const GetStarted = lazy(() => import('@pages/GetStarted/GetStarted'));
const GetStartedAddress = lazy(() => import('@pages/GetStartedAddress/GetStartedAddress'));
const EstimatedAvailableEquity = lazy(() => import('@pages/EstimatedAvailableEquity/EstimatedAvailableEquity'));
const Vmo = lazy(() => import('@pages/Vmo/Vmo'));
const VmoDrawAmount = lazy(() => import('@pages/VmoDrawAmount/VmoDrawAmount'));
const PersonalInformation = lazy(() => import('@pages/PersonalInformation/PersonalInformation'));
const EmploymentInformation = lazy(() => import('@pages/EmploymentInformation/EmploymentInformation'));
const GovernmentMonitoringInformation = lazy(() => import('@pages/GovernmentMonitoringInformation/GovernmentMonitoringInformation'));
const InquirySummary = lazy(() => import('@pages/InquirySummary/InquirySummary'));
const Completion = lazy(() => import('@pages/Completion/Completion'));
const Declarations = lazy(() => import('@pages/Declarations/Declarations'));
const NotFound = lazy(() => import('@pages/NotFound/NotFound'));
const ResumeContinue = lazy(() => import('@/pages/ResumeContinue/ResumeContinue'));
const LineAmountCalculator = lazy(() => import('@pages/LineAmountCalculator/LineAmountCalculator'));
const VmoV2 = lazy(() => import('@/pages/VmoV2/VmoV2'));

import { trackPageView } from '@/config/newrelic';
import { PageAccessProvider } from '@/contexts/PageAccessContext';
import { getErrorProperty } from '@/utils/handleErrors';
import { isDeclarationsVisible } from '@/utils/isDeclarationsVisible';
import { markResumeProgressOnAccess } from '@/utils/markResumeNavigationAccess';
import { DefaultModal } from '@Modals/DefaultModal/DefaultModal';
import { theme } from './Theme';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { PageLoading } from './components/PageLoading/PageLoading';
import ScrollToTop from './components/ScrollTop/ScrollTop';
import { useInjectGtm } from './utils/gtmInject';

const AppRouter = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const experienceService = useMemo(() => new ExperienceService(dispatch, () => store.getState()), [dispatch]);
  const [accessedPages, setAccessedPages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const currentPage = window.location.pathname;

  let templateConfig = useAppSelector((state) => state.TemplateConfig);

  const declarationsVisible = isDeclarationsVisible(templateConfig);

  const currentStep = useAppSelector((state) => state.currentPage.step);
  const pageName = useAppSelector((state) => state.currentPage.value);
  const isGoingBack = useAppSelector(selectIsGoingBack);

  const [errorResponse, setErrorResponse] = useState<unknown>('');
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);

  const [isPageReloaded, setIsPageReloaded] = useState(false);
  const gtag = (...args: any[]) => {
    if (typeof (window as any)._gtmPush === 'function') {
      (window as any)._gtmPush(args);
    } else {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(args);
    }
  };

  const getInitialData = useCallback(async () => {
    try {
      await experienceService.getGlobalConfig();
    } catch (error) {
      console.error(error);
      setErrorResponse(String(error));
      throw error;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useInjectGtm();

  const handlePageAccess = useCallback((page: string) => {
    setAccessedPages((prevPages) => new Set(prevPages).add(page));
  }, []);

  const resumePageAccessValue = useMemo(
    () => ({
      afterResumeNavigateTo: (targetPath: string) => {
        markResumeProgressOnAccess(handlePageAccess, targetPath);
      },
    }),
    [handlePageAccess],
  );

  const setGlobalTheme = () => {
    const resolvedNavBarFontColor = templateConfig?.globalTheme?.navbarFontColor;
    const resolvedSideBarFontColor = templateConfig?.globalTheme?.sidebarColorHeader;
    const resolvedSideBarCompletedColor = templateConfig?.globalTheme?.fillCustomCircleChecked ?? templateConfig?.globalTheme?.sidebarColorStepsChecked ?? theme.colors.primary;

    theme.colors.bgSidebar = templateConfig?.globalTheme?.backgroundSidebarColor ?? theme.colors.bgSidebar;
    theme.colors.primary = templateConfig?.globalTheme?.primaryColor ?? theme.colors.primary;
    theme.colors.secondary = templateConfig?.globalTheme?.secondaryColor ?? theme.colors.secondary;
    theme.colors.textSecondary = templateConfig?.globalTheme?.secondaryColor ?? theme.colors.textSecondary;
    theme.colors.textPrimary = templateConfig?.globalTheme?.primaryColor ?? theme.colors.primary;
    theme.colors.errorColor = templateConfig?.globalTheme?.errorColor ?? theme.colors.errorColor;
    theme.colors.bgLightGrey = templateConfig?.globalTheme?.backgroundColorLight ?? theme.colors.bgLightGrey;
    theme.colors.bgSecondary = templateConfig?.globalTheme?.primaryColor ?? theme.colors.bgSecondary;
    theme.colors.textPrimaryLd = templateConfig?.globalTheme?.primaryColor ?? theme.colors.textPrimaryLd;
    theme.colors.sideBarColorSteps = templateConfig?.globalTheme?.sidebarColorSteps ?? theme.colors.sideBarColorSteps;
    theme.colors.sideBarColorStepsChecked = resolvedSideBarCompletedColor;
    theme.colors.sidebarStrokeCircles = templateConfig?.globalTheme?.sidebarStrokeCircles ?? '#047f30';
    theme.colors.fillCustomCircleCheck = templateConfig?.globalTheme?.fillCustomCircleCheck ?? '#047f30';
    theme.colors.sideBarColorHeader = resolvedSideBarFontColor ?? theme.colors.sideBarColorHeader;
    theme.colors.colorCustomCircleChecked = templateConfig?.globalTheme?.colorCustomCircleChecked ?? '#047f30';
    theme.colors.fillCustomCircleChecked = templateConfig?.globalTheme?.fillCustomCircleChecked || '#ffffff';
    theme.colors.bgSelectProgramButton = templateConfig?.globalTheme?.backgroundColorLight ?? theme.colors.bgSelectProgramButton;
    theme.colors.socialIconsHoverColor = templateConfig?.globalTheme?.socialIconsHoverColor ?? theme.colors.sideBarColorSteps;
    theme.colors.navbarFontColor = resolvedNavBarFontColor ?? theme.colors.navbarFontColor;
  };

  useEffect(() => {
    if (errorResponse) {
      setErrorResponse(errorResponse);
      setOpenErrorModal(true);
    }
  }, [errorResponse, setErrorResponse]);

  useEffect(() => {
    if (Object.keys(templateConfig).length && templateConfig?.lenderId) {
      setGlobalTheme();
      setLoading(false);
    }
    document.title = templateConfig.lenderName || 'First Close';
  }, [templateConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const notIndexPages = ['/get-started', '/resume/continue'];

  const formatPath = (path: string): string => {
    const formattedPath = path.trimStart() ? path.slice(1) : path;
    return formattedPath
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const createVmoV1Arrays = (isVmoDrawAmountEnabled: boolean) => {
    const pages = ['vmo-line-amount'];
    const components = [Vmo, LineAmountCalculator];

    if (isVmoDrawAmountEnabled) {
      pages.push('vmo-draw-amount');
      components.push(VmoDrawAmount);
    }

    return { pages, components };
  };

  const NOT_FOUND_PAGE = '/not-found';
  const HOME_PAGE = '/';
  const isNotSpecialPage = currentPage !== NOT_FOUND_PAGE && currentPage !== HOME_PAGE;
  const isNotIndexPage = !notIndexPages.includes(currentPage);

  const isVmoV2 = templateConfig?.features?.vmoRedesign?.isEnabled ?? false;
  const { pages: vmoV1Pages } = createVmoV1Arrays(templateConfig?.pages?.hasDrawAmountPage ?? false);
  const { components: vmoV1Components } = createVmoV1Arrays(templateConfig?.pages?.hasDrawAmountPage ?? false);
  const vmoV2Components = [VmoV2];
  const vmoComponentsList = isVmoV2 ? vmoV1Components : vmoV2Components;

  const componentsList: any = [
    GetStarted,
    GetStartedAddress,
    EstimatedAvailableEquity,
    VmoV2,
    Vmo,
    LineAmountCalculator,
    VmoDrawAmount,
    PersonalInformation,
    EmploymentInformation,
    GovernmentMonitoringInformation,
    Declarations,
    InquirySummary,
    Completion,
  ];

  const filterList = componentsList.filter((component: FC) => {
    if (component === Declarations) return declarationsVisible;
    return !vmoComponentsList.includes(component);
  });

  const filterPages = isVmoV2
    ? pageListOrder.filter((page) => {
        if (page === 'declarations') return declarationsVisible;
        return !vmoV1Pages.includes(page);
      })
    : pageListOrder.filter((page) => {
        if (page === 'declarations') return declarationsVisible;
        return true;
      });

  useEffect(() => {
    if (isNotSpecialPage) {
      const formattedPath = formatPath(window.location.pathname);
      dispatch(addCurrentPage(formattedPath));
      if (isNotIndexPage && isPageReloaded && !isGoingBack && formattedPath !== pageName) {
        dispatch(incrementStep());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotSpecialPage, isNotIndexPage, isPageReloaded, dispatch, currentPage]);

  useEffect(() => {
    const hasVisited = sessionStorage.getItem('hasVisited');

    if (!hasVisited) {
      sessionStorage.setItem('hasVisited', 'true');
    }

    if (hasVisited && isGoingBack) {
      dispatch(resetGoingBack());
    }

    if (!isPageReloaded) {
      setIsPageReloaded(true);
    }

    setTimeout(() => {
      if (currentPage) {
        trackPageView(currentPage);
      }
    }, 100);

    gtag('event', 'screen_view', {
      pageId: currentPage,
      stepNumber: `Step ${currentStep} Page ${pageName}`,
      pageName: pageName,
    });

    const pagePath = window.location.pathname;
    const pageLocation = window.location.href;
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get('utm_source') || undefined;
    const utmMedium = searchParams.get('utm_medium') || undefined;
    const utmCampaign = searchParams.get('utm_campaign') || undefined;
    const utmContent = searchParams.get('utm_content') || undefined;

    const virtualPageviewEvent: Record<string, any> = {
      event: 'VirtualPageview',
      pagePath,
      pageLocation,
      pageTitle: pageName,
      pageName,
      pageId: currentPage,
      stepNumber: `Step ${currentStep} Page ${pageName}`,
    };

    if (utmSource) {
      virtualPageviewEvent.utm_source = utmSource;
    }
    if (utmMedium) {
      virtualPageviewEvent.utm_medium = utmMedium;
    }
    if (utmCampaign) {
      virtualPageviewEvent.utm_campaign = utmCampaign;
    }
    if (utmContent) {
      virtualPageviewEvent.utm_content = utmContent;
    }

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(virtualPageviewEvent);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dispatch, isGoingBack]);

  const canAccessPage = useCallback(
    (page: string) => {
      const currentIndex = filterPages.indexOf(page);
      const previousPages = filterPages.slice(0, currentIndex);
      return previousPages.every((prevPage) => accessedPages.has(prevPage));
    },
    [accessedPages, filterPages],
  );

  const preserveQueryParams = (path: string) => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  };
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const renderProtectedRoute = (path: string, Component: FC<{ onAccess?: (page: string) => void }>, isProtected: boolean): ReactNode => (
    <Route key={path} path={path} element={<ProtectedRoute element={<Component onAccess={handlePageAccess} />} isProtected={isProtected} />} />
  );

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      {openErrorModal &&
        errorResponse &&
        (() => {
          const header = getErrorProperty(errorResponse, 'appActionTitle') || 'Error';
          const message = getErrorProperty(errorResponse, 'appActionMessage');
          const buttonTitle = getErrorProperty(errorResponse, 'appActionButtonTitle') || 'Return to %%lenderName%%';

          const body = message || (typeof errorResponse === 'string' ? errorResponse : String(errorResponse || 'An error occurred'));

          return (
            <DefaultModal
              openModal={openErrorModal}
              setOpenModal={setOpenErrorModal}
              isErrorModal={true}
              infoModal={{
                header,
                body,
                button: buttonTitle,
              }}
              preventBackgroundInteraction={true}
            />
          );
        })()}
      <PageAccessProvider value={resumePageAccessValue}>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/" element={<Navigate to={preserveQueryParams('/get-started')} replace />} />
              <Route path="resume/continue" element={<ResumeContinue />} />
              {filterPages?.map((page, index) => renderProtectedRoute(page, filterList[index], canAccessPage(page)))}
              <Route path="not-found" element={<NotFound />} />
              <Route path="*" element={<Navigate to={preserveQueryParams('/get-started')} replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </PageAccessProvider>
    </>
  );
};

export default AppRouter;
